import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { SearchSharp } from '@mui/icons-material';
import useStore from 'src/store/cns';
import { validateInput } from 'src/utils/common';
import { useNavigate } from 'react-router-dom';

const Search: React.FC<{
  setRegisterState: (state: RegisterState) => void;
  domainName: string;
}> = ({ setRegisterState, domainName }) => {
  const wallet = useStore(state => state.wallet);
  const firstLvlDomains = useStore(state => state.firstLvlDomains);

  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState<string>(domainName);
  const [callState, setCallState] = useState<
    'idle' | 'available' | 'taken' | 'too_short'
  >('idle');

  const handleInputChange = (event: any) => {
    const input = event.target.value;
    if (validateInput(input)) {
      setSearchQuery(input);
      if (input.length > 0 && input.length <= 3) {
        setCallState('too_short');
      } else if (firstLvlDomains.find(domain => domain === input)) {
        setCallState('taken');
      } else if (input.length === 0) {
        setCallState('idle');
      } else {
        setCallState('available');
      }
    }
  };

  const openDomain = () => {
    setRegisterState({
      domainName: searchQuery + '.cam',
      status: 'information',
    });
  };

  const handleClick = () => {
    setRegisterState({
      domainName: searchQuery,
      status: 'registering',
    });
  };

  const renderButtons = () => {
    if (wallet && searchQuery.length !== 0 && callState === 'available') {
      return (
        <Button
          variant="contained"
          onClick={() => handleClick()}
          sx={{
            color: 'text.primary',
            backgroundColor: 'primary.main',
            padding: '10px 16px',
          }}
        >
          Register
        </Button>
      );
    } else if (searchQuery.length !== 0) {
      return (
        <Button
          variant="contained"
          onClick={() => {
            setRegisterState({
              status: 'registering',
              domainName: searchQuery,
            }),
              console.log('Navigate to /wallet');
            navigate('/wallet');
          }}
          sx={{
            color: 'text.primary',
            backgroundColor: 'primary.main',
            padding: '10px 16px',
          }}
        >
          Connect Wallet
        </Button>
      );
    }
  };

  return (
    <Container maxWidth="lg">
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography
          data-cy="sd-title"
          variant="h2"
          align="center"
          color="text.primary"
          gutterBottom
        >
          Search Your CNS Domain
        </Typography>
        <Typography
          data-cy="sd-subtitle"
          variant="h4"
          align="center"
          color="text.primary"
          sx={{ mb: 2 }}
        >
          Linking Identities to Wallets: Unifying Names, Information & Entities
        </Typography>
        <Box mt={3} width="45%">
          <TextField
            fullWidth
            value={searchQuery}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchSharp />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Typography variant="body1" sx={{ color: 'info.main' }}>
                    .cam
                  </Typography>
                </InputAdornment>
              ) : null,
            }}
            placeholder="Search .cam domain"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '48px',
                '&:hover fieldset': {
                  borderColor: 'grey.600',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'grey.600',
                },
              },
            }}
            onChange={handleInputChange}
          />
        </Box>
        {
          <Box
            sx={{
              height: '25px',
              visibility: callState !== 'idle' ? 'visible' : 'hidden',
            }}
          >
            {callState === 'available' ? (
              <Box mt={4}>{renderButtons()}</Box>
            ) : callState === 'taken' ? (
              <Typography mt={4} data-cy="sd-domain-taken" color="error.main">
                Domain Taken
                <Button
                  onClick={openDomain}
                  sx={{
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'grey.800',
                      borderColor: 'grey.600',
                    },
                  }}
                >
                  <Typography variant="body1">Open</Typography>
                </Button>
              </Typography>
            ) : (
              callState === 'too_short' && (
                <Typography mt={4} data-cy="sd-invalid-name" color="error.main">
                  Must have more than 3 characters
                </Typography>
              )
            )}
          </Box>
        }
      </Box>
    </Container>
  );
};

export default React.memo(Search);
